import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  ScriptableContext,
  ChartArea,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ReactElement } from 'react';
import { Loader } from '@sc/shared-ui';
import { fiveTickScale } from '../utility/chartScale';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const LINE_COLOR = '#3eb077';

// Fades the area fill from a translucent tint at the top to transparent at the
// baseline, matching the "Tokens" chart in the reference design.
const areaGradient = (
  context: ScriptableContext<'line'>
): CanvasGradient | string => {
  const { ctx } = context.chart;
  // chart.js types chartArea as always-present, but it is undefined on the
  // first paint before the layout is measured — fall back to a flat tint.
  const chartArea = context.chart.chartArea as ChartArea | undefined;

  if (!chartArea) return `${LINE_COLOR}4d`;

  const gradient = ctx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom
  );

  gradient.addColorStop(0, `${LINE_COLOR}4d`);

  gradient.addColorStop(1, `${LINE_COLOR}00`);

  return gradient;
};

interface Props {
  name: string;
  loading: boolean;
  data: Array<{ label: string; count: number }>;
}

export const LineGraph = ({ name, loading, data }: Props): ReactElement => {
  const scale = fiveTickScale(data.map((d) => d.count));

  return (
    <div className="p-5 rounded-md flex flex-col">
      <div className="flex items-center mb-5 space-x-5">
        <h4 className="text-h2 font-grotesque font-semibold">{name}</h4>
        {loading && <Loader className="size-5" />}
      </div>
      <div className="h-48 w-full">
        <Line
          options={{
            maintainAspectRatio: false,
            interaction: {
              mode: 'index',
              intersect: false,
            },
            plugins: {
              legend: {
                display: false,
              },
            },
            scales: {
              x: {
                grid: {
                  display: false,
                },
                ticks: {
                  color: '#879099',
                  font: {
                    size: 14,
                    family: 'Open Sans',
                  },
                },
              },
              y: {
                beginAtZero: true,
                max: scale.max,
                grid: {
                  color: '#292A2F',
                },
                border: {
                  display: false,
                },
                ticks: {
                  stepSize: scale.stepSize,
                  color: '#879099',
                  font: {
                    size: 14,
                    family: 'Open Sans',
                  },
                },
              },
            },
          }}
          data={{
            labels: data.map((d) => d.label),
            datasets: [
              {
                label: name,
                data: data.map((d) => d.count),
                borderColor: LINE_COLOR,
                backgroundColor: areaGradient,
                pointBackgroundColor: LINE_COLOR,
                fill: 'origin',
                tension: 0.4,
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 4,
              },
            ],
          }}
        />
      </div>
    </div>
  );
};
